/**
 * Cupid tank particle effects — mark application, heartbeat impacts, HEARTBREAK detonation.
 *
 * Color palette: pink 0xff5588, hot pink 0xff3366, soft pink 0xff88aa,
 * deep rose 0xff2266, white 0xffffff.
 */

import type { ParticleCore } from '../types';

/**
 * Arrow mark-application flash — pulsing pink/white ring + heart-shaped particle burst.
 * Communicates "you are now marked" with a distinct visual signature.
 * Called when Cupid's Arrow hits and applies cupidMarkTurns.
 */
export function spawnCupidMarkApplied(core: ParticleCore, x: number, y: number): void {
  const now = Date.now();

  // Phase 0: Bright white-pink flash at target
  core.spawnParticle(x, y, 0xffffff, 0, 1.5, 0.06, 0.1, 1.5, 2.5, 0, 0.3, true, true, 0.9);
  core.spawnParticle(x, y, 0xff5588, 0, 1.0, 0.08, 0.12, 1.0, 2.0, 0, 0.4, true, false, 0.7);

  // Pink shockwave ring — expanding "mark applied" pulse
  core.spawnShockwave(x, y, 0xff5588, 50, 220, 2.5, 0.15);

  // Inner white ring (tighter, faster)
  core.spawnShockwave(x, y, 0xffffff, 25, 180, 1.5, 0.08);

  // Heart-shaped particle burst — 16 particles arranged in heart parametric curve
  for (let i = 0; i < 16; i++) {
    const t = (i / 16) * Math.PI * 2;
    const sinT = Math.sin(t);
    const hx = 16 * sinT * sinT * sinT;
    const hy = -(13 * Math.cos(t) - 5 * Math.cos(2 * t) - 2 * Math.cos(3 * t) - Math.cos(4 * t));
    // Normalize and scale to ~30px radius
    const nx = (hx / 16) * 30;
    const ny = (hy / 16) * 30;
    const speed = 2 + Math.random() * 3;
    const outAngle = Math.atan2(ny, nx);
    const r = Math.random();
    const color = r < 0.3 ? 0xffffff : r < 0.7 ? 0xff5588 : 0xff88aa;
    core.spawnParticleAtAngle(
      x + nx * 0.3, y + ny * 0.3,
      outAngle, speed,
      color,
      0.12 + Math.random() * 0.08,
      0.3 + Math.random() * 0.2,
      0, 0.93,
      true, true,
      0.85,
      1.5, // mild stretch
    );
  }

  // Impact lines — 6 pink/white radial streaks
  for (let i = 0; i < 6; i++) {
    const angle = (i / 6) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
    const life = 0.1 + Math.random() * 0.04;
    core.impactLines.push({
      x, y, angle,
      innerRadius: 3,
      outerRadius: 20 + Math.random() * 12,
      expandSpeed: 180 + Math.random() * 50,
      color: Math.random() < 0.3 ? 0xffffff : 0xff5588,
      lineWidth: 2 + Math.random(),
      life, maxLife: life,
      freezeImmune: false,
    });
  }

  // Delayed sparkle twinkles (mark "settling in")
  for (let i = 0; i < 5; i++) {
    const delay = 100 + Math.random() * 200;
    const sx = x + (Math.random() - 0.5) * 50;
    const sy = y + (Math.random() - 0.5) * 40;
    core.pendingBlasts.push({
      timestamp: now + delay,
      callback: () => {
        core.spawnParticle(
          sx, sy,
          Math.random() < 0.5 ? 0xff88aa : 0xffffff,
          0, 0.3,
          0.06, 0.12,
          0.25, 0.5,
          0, 0.9,
          true, true,
          0.8,
        );
      },
    });
  }
}

/**
 * Heartbeat impact burst — satisfying hit VFX at each damaged player's position.
 * Scale depends on whether target was marked (isMarked = bigger, different color).
 * @param x - Hit player X
 * @param y - Hit player Y
 * @param isMarked - Whether target had cupidMarkTurns > 0
 * @param isBeat2 - Whether this is beat 2 (bigger)
 */
export function spawnCupidHeartbeatHit(
  core: ParticleCore,
  x: number, y: number,
  isMarked: boolean,
  isBeat2: boolean,
): void {
  const scale = isBeat2 ? 1.3 : 1.0;
  const markScale = isMarked ? 1.5 : 1.0;
  const totalScale = scale * markScale;

  // Primary color shifts deeper when marked
  const primaryColor = isMarked ? 0xff2266 : (isBeat2 ? 0xff3366 : 0xff5588);
  const accentColor = isMarked ? 0xff0044 : 0xff88aa;

  // Core burst — white-hot center
  const coreCount = Math.round(8 * totalScale);
  for (let i = 0; i < coreCount; i++) {
    core.spawnParticle(
      x + (Math.random() - 0.5) * 8,
      y + (Math.random() - 0.5) * 8,
      0xffffff,
      1, 5 * totalScale,
      0.06, 0.12,
      0.6 * totalScale, 1.5 * totalScale,
      0, 0.88,
      true, true,
      0.9,
    );
  }

  // Ring burst — pink particles at even angles
  const ringCount = Math.round(10 * totalScale);
  for (let i = 0; i < ringCount; i++) {
    const angle = (i / ringCount) * Math.PI * 2;
    const r = Math.random();
    const color = r < 0.3 ? 0xffffff : r < 0.7 ? primaryColor : accentColor;
    core.spawnParticleAtAngle(
      x, y,
      angle, 5 + Math.random() * 4 * totalScale,
      color,
      0.1 + Math.random() * 0.06,
      0.3 + Math.random() * 0.2,
      0, 0.93,
      true, true,
      0.85,
      1.8, // stretch
    );
  }

  // Shockwave
  core.spawnShockwave(x, y, primaryColor, 35 * totalScale, 220, 2.5, 0.12);

  // Impact lines — proportional to scale
  const lineCount = Math.round(4 * totalScale);
  for (let i = 0; i < lineCount; i++) {
    const angle = (i / lineCount) * Math.PI * 2 + (Math.random() - 0.5) * 0.3;
    const life = 0.08 + Math.random() * 0.04;
    core.impactLines.push({
      x, y, angle,
      innerRadius: 3,
      outerRadius: 18 + Math.random() * 10,
      expandSpeed: 180 + Math.random() * 50,
      color: Math.random() < 0.3 ? 0xffffff : primaryColor,
      lineWidth: 2 + Math.random(),
      life, maxLife: life,
      freezeImmune: false,
    });
  }

  // Marked targets get extra ember rain
  if (isMarked) {
    for (let i = 0; i < 6; i++) {
      const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.6;
      core.spawnParticleAtAngle(
        x + (Math.random() - 0.5) * 8,
        y,
        angle,
        3 + Math.random() * 5,
        Math.random() < 0.5 ? 0xff3366 : 0xff88aa,
        0.2 + Math.random() * 0.15,
        0.15 + Math.random() * 0.1,
        0.08, // gravity — drifts down
        0.96,
        false, true,
        0.6,
      );
    }
  }
}

/**
 * HEARTBREAK detonation — combo finisher when marked target is hit by both beats.
 * Duelist tipper-level impact: massive flash, eruption, celebration.
 * This is the payoff moment for Cupid's mark-then-punish loop.
 */
export function spawnCupidHeartbreak(core: ParticleCore, x: number, y: number): void {
  const now = Date.now();

  // Phase 0: Freeze-immune hit cross — deep rose, large
  for (let i = 0; i < 4; i++) {
    const angle = (i / 4) * Math.PI * 2 + Math.PI / 4;
    const life = 0.3;
    core.impactLines.push({
      x, y, angle,
      innerRadius: 0,
      outerRadius: 50,
      expandSpeed: 35,
      color: 0xff2266,
      lineWidth: 6,
      life, maxLife: life,
      freezeImmune: true,
    });
  }
  // Secondary cross (thinner, different angle)
  for (let i = 0; i < 4; i++) {
    const angle = (i / 4) * Math.PI * 2;
    const life = 0.25;
    core.impactLines.push({
      x, y, angle,
      innerRadius: 0,
      outerRadius: 35,
      expandSpeed: 25,
      color: 0xff5588,
      lineWidth: 4,
      life, maxLife: life,
      freezeImmune: true,
    });
  }

  // Phase 1: Core detonation — white-hot burst
  for (let i = 0; i < 20; i++) {
    core.spawnParticle(
      x + (Math.random() - 0.5) * 10,
      y + (Math.random() - 0.5) * 10,
      0xffffff,
      2, 7,
      0.08, 0.16,
      1.0, 2.2,
      0, 0.88,
      true, true,
      0.95,
    );
  }

  // Pink ring burst — 14 particles at even angles
  for (let i = 0; i < 14; i++) {
    const angle = (i / 14) * Math.PI * 2;
    const r = Math.random();
    const color = r < 0.3 ? 0xff2266 : r < 0.6 ? 0xff0044 : 0xff5588;
    core.spawnParticleAtAngle(
      x, y,
      angle, 9 + Math.random() * 4,
      color,
      0.12 + Math.random() * 0.06,
      0.5 + Math.random() * 0.3,
      0, 0.93,
      true, true,
      0.9,
      2, // stretch
    );
  }

  // Concentric shockwaves — white inner, pink outer
  core.spawnShockwave(x, y, 0xffffff, 55, 320, 3, 0.15);
  core.spawnShockwave(x, y, 0xff2266, 80, 260, 4, 0.2);

  // Phase 2: Eruption (t=80ms) — dense pink particle cloud
  core.pendingBlasts.push({
    timestamp: now + 80,
    callback: () => {
      for (let i = 0; i < 40; i++) {
        const r = Math.random();
        const color = r < 0.3 ? 0xff2266 : r < 0.6 ? 0xff5588 : r < 0.8 ? 0xff3366 : 0xff88aa;
        const useAdd = Math.random() < 0.5;
        core.spawnParticle(
          x + (Math.random() - 0.5) * 16,
          y + (Math.random() - 0.5) * 16,
          color,
          1, 6,
          0.15, 0.3,
          0.4, 1.0,
          0.02, 0.94,
          useAdd, true,
          0.8,
        );
      }

      // Pink impact lines — thick, expanding fast
      for (let i = 0; i < 8; i++) {
        const angle = (i / 8) * Math.PI * 2 + (Math.random() - 0.5) * 0.2;
        const life = 0.12 + Math.random() * 0.05;
        core.impactLines.push({
          x, y, angle,
          innerRadius: 5,
          outerRadius: 28 + Math.random() * 18,
          expandSpeed: 240 + Math.random() * 80,
          color: Math.random() < 0.3 ? 0xffffff : 0xff2266,
          lineWidth: 3 + Math.random(),
          life, maxLife: life,
          freezeImmune: false,
        });
      }

      // Rose ember rain — gravity, upward arc
      for (let i = 0; i < 12; i++) {
        const angle = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI * 0.8;
        core.spawnParticleAtAngle(
          x + (Math.random() - 0.5) * 10,
          y,
          angle,
          4 + Math.random() * 7,
          Math.random() < 0.5 ? 0xff3366 : 0xff88aa,
          0.3 + Math.random() * 0.25,
          0.2 + Math.random() * 0.15,
          0.12, // gravity
          0.97,
          false, true,
          0.7,
        );
      }
    },
  });

  // Phase 3: Celebration (t=200-500ms) — white sparkle twinkles
  for (let i = 0; i < 7; i++) {
    const delay = 200 + Math.random() * 300;
    const sx = x + (Math.random() - 0.5) * 70;
    const sy = y + (Math.random() - 0.5) * 50;
    core.pendingBlasts.push({
      timestamp: now + delay,
      callback: () => {
        core.spawnParticle(
          sx, sy,
          Math.random() < 0.5 ? 0xffffff : 0xff88aa,
          0, 0.3,
          0.06, 0.12,
          0.3, 0.6,
          0, 0.9,
          true, true,
          0.9,
        );
      },
    });
  }

  // Faint pink smoke drift
  core.pendingBlasts.push({
    timestamp: now + 250,
    callback: () => {
      for (let i = 0; i < 4; i++) {
        core.spawnParticle(
          x + (Math.random() - 0.5) * 30,
          y + (Math.random() - 0.5) * 20,
          Math.random() < 0.4 ? 0x331122 : 0x220011,
          0.3, 1.0,
          0.3, 0.5,
          1.0, 1.8,
          -0.01, // float up
          0.98,
          false, false,
          0.35,
        );
      }
    },
  });
}
